import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LockKey,
  Fire,
  Timer,
  Eye,
  ArrowRight,
  Code,
  Key,
  ShieldCheck,
  Warning,
} from "@phosphor-icons/react";

import { encryptText } from "@/lib/crypto";
import { createPaste } from "@/lib/api";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const EXPIRATIONS = [
  { value: "10m", label: "10 minutes" },
  { value: "1h", label: "1 hour" },
  { value: "1d", label: "1 day" },
  { value: "1w", label: "1 week" },
  { value: "1mo", label: "1 month" },
  { value: "never", label: "Never" },
];

const LANGUAGES = [
  "plain",
  "javascript",
  "typescript",
  "python",
  "go",
  "rust",
  "java",
  "c",
  "cpp",
  "shell",
  "json",
  "yaml",
  "html",
  "css",
  "sql",
  "markdown",
];

export default function CreatePaste() {
  const navigate = useNavigate();
  const [content, setContent] = useState("");
  const [expiration, setExpiration] = useState("1d");
  const [viewLimit, setViewLimit] = useState("");
  const [burn, setBurn] = useState(false);
  const [language, setLanguage] = useState("plain");
  const [password, setPassword] = useState("");
  const [usePassword, setUsePassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const charCount = content.length;
  const lineCount = useMemo(() => content.split("\n").length, [content]);
  const sizeKb = useMemo(
    () => (new Blob([content]).size / 1024).toFixed(2),
    [content],
  );

  const handleSubmit = async () => {
    if (!content.trim()) {
      toast.error("Cannot encrypt an empty paste.");
      return;
    }
    if (usePassword && password.length < 4) {
      toast.error("Password must be at least 4 characters.");
      return;
    }

    setSubmitting(true);
    try {
      const { ciphertext, iv, keyB64 } = await encryptText(
        content,
        usePassword ? password : null,
      );
      const payload = {
        ciphertext,
        iv,
        has_password: usePassword,
        expiration,
        view_limit: viewLimit ? Math.max(1, parseInt(viewLimit, 10)) : null,
        burn_after_read: burn,
        language: language === "plain" ? null : language,
      };

      const data = await createPaste(payload);
      // Navigate to the share screen; key goes into URL fragment (never sent)
      navigate(`/p/${data.id}/share#${keyB64}`, {
        state: { meta: data, keyB64 },
      });
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.detail || "Failed to create encrypted paste.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-8 py-10">
      {/* Hero */}
      <div className="mb-10 animate-fade-up">
        <div className="label-overline mb-3" data-testid="page-label">
          //  Step 01 / Compose
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tighter leading-[0.95]">
          Paste it. Lock it.
          <br />
          <span className="text-muted-foreground">Disappear it.</span>
        </h1>
        <p className="mt-4 font-mono text-sm text-muted-foreground max-w-2xl">
          Content is encrypted in your browser with AES-256-GCM before it ever
          leaves the device. The server adds a second Fernet layer, and the
          decryption key lives only in the URL you share.
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Editor */}
        <div className="lg:col-span-2 border border-foreground/15 bg-card">
          <div className="flex items-center justify-between border-b border-foreground/15 px-4 py-3">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 bg-secure rounded-full animate-pulse-dot" />
              <span
                className="label-overline"
                data-testid="editor-status-label"
              >
                Plaintext Buffer · local only
              </span>
            </div>
            <div className="font-mono text-[11px] text-muted-foreground flex gap-4">
              <span data-testid="char-count">{charCount} chars</span>
              <span data-testid="line-count">{lineCount} lines</span>
              <span data-testid="size-kb">{sizeKb} KB</span>
            </div>
          </div>
          <textarea
            data-testid="paste-content-textarea"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={`// Type or paste anything here.\n// Encryption happens before it leaves your browser.\n\nconst secret = "🔒 only the URL holder can decrypt this";`}
            spellCheck={false}
            className="w-full min-h-[58vh] bg-transparent p-5 font-mono text-sm text-foreground placeholder:text-muted-foreground/60 outline-none resize-none"
          />
          <div className="border-t border-foreground/15 px-4 py-3 flex items-center justify-between text-[11px] font-mono text-muted-foreground">
            <span>
              <span className="text-foreground font-semibold">/dev/null</span>{" "}
              ← what the server sees of your plaintext
            </span>
            <span className="hidden md:inline">CTRL + ENTER to encrypt</span>
          </div>
        </div>

        {/* Settings */}
        <aside className="border border-foreground/15 bg-card flex flex-col">
          <div className="border-b border-foreground/15 px-5 py-4">
            <div className="label-overline">Encryption Settings</div>
            <div className="mt-2 font-display text-xl font-bold tracking-tight">
              Configure the vault
            </div>
          </div>

          <div className="p-5 space-y-6 flex-1">
            {/* Expiration */}
            <div>
              <Label className="label-overline flex items-center gap-2 mb-2">
                <Timer size={14} weight="bold" /> Expiration
              </Label>
              <Select value={expiration} onValueChange={setExpiration}>
                <SelectTrigger
                  data-testid="expiration-select"
                  className="rounded-none font-mono h-10 border-foreground/20"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none font-mono">
                  {EXPIRATIONS.map((e) => (
                    <SelectItem key={e.value} value={e.value}>
                      {e.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* View limit */}
            <div>
              <Label className="label-overline flex items-center gap-2 mb-2">
                <Eye size={14} weight="bold" /> View limit (optional)
              </Label>
              <Input
                data-testid="view-limit-input"
                type="number"
                min="1"
                max="1000"
                value={viewLimit}
                onChange={(e) => setViewLimit(e.target.value)}
                placeholder="∞"
                className="rounded-none font-mono h-10 border-foreground/20"
              />
              <p className="text-[10px] font-mono text-muted-foreground mt-1.5">
                Paste auto-destroys after N reads.
              </p>
            </div>

            {/* Language */}
            <div>
              <Label className="label-overline flex items-center gap-2 mb-2">
                <Code size={14} weight="bold" /> Syntax hint
              </Label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger
                  data-testid="language-select"
                  className="rounded-none font-mono h-10 border-foreground/20"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none font-mono max-h-72">
                  {LANGUAGES.map((l) => (
                    <SelectItem key={l} value={l}>
                      {l}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Burn after read */}
            <div className="flex items-start justify-between gap-4 pt-2 border-t border-foreground/10">
              <div>
                <Label className="label-overline flex items-center gap-2 mb-1">
                  <Fire size={14} weight="bold" /> Burn after reading
                </Label>
                <p className="text-[10px] font-mono text-muted-foreground max-w-[16rem]">
                  Destroys the paste the moment it is opened.
                </p>
              </div>
              <Switch
                data-testid="burn-toggle"
                checked={burn}
                onCheckedChange={setBurn}
                className="data-[state=checked]:bg-burn"
              />
            </div>

            {/* Password */}
            <div className="pt-2 border-t border-foreground/10">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <Label className="label-overline flex items-center gap-2 mb-1">
                    <Key size={14} weight="bold" /> Password protect
                  </Label>
                  <p className="text-[10px] font-mono text-muted-foreground max-w-[16rem]">
                    Recipient must enter this to decrypt.
                  </p>
                </div>
                <Switch
                  data-testid="password-toggle"
                  checked={usePassword}
                  onCheckedChange={setUsePassword}
                />
              </div>
              {usePassword && (
                <Input
                  data-testid="password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="min 4 characters"
                  className="rounded-none font-mono h-10 border-foreground/20 mt-3"
                />
              )}
            </div>
          </div>

          <button
            data-testid="encrypt-submit-btn"
            onClick={handleSubmit}
            disabled={submitting || !content.trim()}
            className="group h-14 w-full bg-foreground text-background font-bold uppercase tracking-[0.18em] text-xs flex items-center justify-center gap-3 hover:bg-neutral-800 disabled:bg-neutral-300 disabled:text-neutral-500 transition-colors border-t border-foreground/15"
          >
            <LockKey size={16} weight="bold" />
            {submitting ? "Encrypting…" : "Encrypt & Generate Link"}
            <ArrowRight
              size={16}
              weight="bold"
              className="transition-transform group-hover:translate-x-1"
            />
          </button>
        </aside>
      </div>

      {/* Trust strip */}
      <div className="mt-10 grid md:grid-cols-3 gap-px border border-foreground/15 bg-foreground/15">
        <TrustCell
          icon={<LockKey size={18} weight="bold" />}
          title="Zero-knowledge"
          body="Encryption key never touches our servers. It lives in the URL fragment, which browsers don't transmit."
        />
        <TrustCell
          icon={<ShieldCheck size={18} weight="bold" />}
          title="Dual-layer"
          body="Browser-side AES-256-GCM is wrapped again by Fernet (AES-128-CBC + HMAC) on the server."
        />
        <TrustCell
          icon={<Warning size={18} weight="bold" />}
          title="Ephemeral by design"
          body="Burn-after-read, view limits, and TTL expiry guarantee secrets do not linger."
        />
      </div>
    </div>
  );
}

function TrustCell({ icon, title, body }) {
  return (
    <div className="bg-background p-6">
      <div className="flex items-center gap-2 mb-2">{icon}
        <div className="font-display font-bold text-sm tracking-tight">
          {title}
        </div>
      </div>
      <p className="font-mono text-xs text-muted-foreground leading-relaxed">
        {body}
      </p>
    </div>
  );
}
